import type { Document } from '@/questionnaire/types'
import httpClient from '@/utils/httpClient'

interface FetchDocumentType {
  document: {
    template_variable_values: {
      template_variable_id: string
      content: string
    }[]
  } & Document
}

export const createDocument = async (name: string, templateID: string): Promise<Document> => {
  const response = await httpClient.post<{ document: Document }>(`/v1/templates/${templateID}/documents`, { name })
  return response.data.document
}

export const fetchDocument = async (documentId: string): Promise<FetchDocumentType> => {
  const response = await httpClient.get(`/v1/documents/${documentId}`)

  return { ...response.data }
}

export const fetchAll = async (archived: boolean = false): Promise<Document[]> => {
  const response = await httpClient.get(`/v1/documents`, { params: { archived } })
  return response.data.documents
}

export const updateDocument = async (documentId: string, variableValues: Array<Object>, data: Object): Promise<Document> => {
  const response = await httpClient.put(`/v1/documents/${documentId}`, { document: { ...data, variable_values: variableValues } })

  return response.data
}

export const downloadDocument = (documentId: string, documentName: string) => {
  return httpClient
    .get(`/v1/documents/${documentId}/pdf`)
    .then(res => {
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${documentName}.pdf`)
      document.body.appendChild(link)
      link.click()
    })
    .catch(error => {
      return Promise.reject(error.response)
    })
}

export const updateStatus = async (
  documentId: string,
  workflow_progress: 'finalize' | 'revert_to_draft'
): Promise<{
  document?: Document
  error?: string
}> => {
  return await httpClient
    .put(`/v1/documents/${documentId}`, { document: { workflow_progress } })
    .then(({ data }) => {
      return { document: data?.document }
    })
    .catch(error => {
      return { error: error.response?.data?.error }
    })
}

export const archiveDocument = async (documentId: string): Promise<Document> => {
  const res = await httpClient.delete<Document>(`/v1/documents/${documentId}/archive`)
  return res.data
}

export const unarchiveDocument = async (documentId: string): Promise<Document> => {
  const res = await httpClient.put<Document>(`/v1/documents/${documentId}/unarchive`)
  return res.data
}

export default {
  fetchAll,
  downloadDocument,
  updateDocument,
  fetchDocument,
  createDocument,
  updateStatus,
  archiveDocument,
  unarchiveDocument,
}
